import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import BookingCalendar from './BookingCalendar';

/**
 * A date field that opens a calendar in a popover when clicked.
 * Shows "Check-in — Check-out" or placeholder. Professional dropdown style.
 */
const DateFieldWithCalendar = ({
  checkIn = '',
  checkOut = '',
  onChange,
  blockedDates = [],
  packages = [],
  language = 'en',
  placeholder = 'Select dates',
  placeholderAr = 'اختر التواريخ',
  minNights = 1,
  monthsAhead = 12,
  disabled = false,
  className = ''
}) => {
  const lang = language?.slice(0, 2) === 'ar' ? 'ar' : 'en';
  const [open, setOpen] = useState(false);
  const [partialPackageMessage, setPartialPackageMessage] = useState('');
  const containerRef = useRef(null);

  const popoverPosition =
    open && containerRef.current && typeof window !== 'undefined'
      ? (() => {
          const rect = containerRef.current.getBoundingClientRect();
          const popoverWidth = 600;
          const maxLeft = window.innerWidth - popoverWidth - 16;
          return {
            left: Math.max(8, Math.min(rect.left, maxLeft)),
            top: rect.bottom + 8
          };
        })()
      : { left: 0, top: 0 };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (!containerRef.current?.contains(e.target) && !e.target.closest('[data-calendar-popover]')) {
        setOpen(false);
        setPartialPackageMessage('');
      }
    };
    const t = setTimeout(() => document.addEventListener('click', handleClickOutside), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [open]);

  const formatDate = (ymd) => {
    if (!ymd) return '';
    const d = new Date(ymd + 'T12:00:00');
    return d.toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const label =
    checkIn && checkOut
      ? `${formatDate(checkIn)} — ${formatDate(checkOut)}`
      : lang === 'ar'
        ? placeholderAr
        : placeholder;

  const handleCalendarChange = (newCheckIn, newCheckOut) => {
    setPartialPackageMessage('');
    onChange(newCheckIn, newCheckOut);
    // Calendar stays open until user clicks outside (no auto-close on date select)
  };

  const partialMsgText =
    lang === 'ar'
      ? 'لا يمكن حجز جزء من باقة. اختر إما كل أيام الباقة (كل الليالي) أو أياماً خارج الباقات فقط.'
      : 'You cannot book a partial package. Select either the full package dates (all nights) or only dates outside packages.';

  const popoverContent = open && (
    <div
      data-calendar-popover
      className="fixed z-[100] rounded-2xl overflow-hidden shadow-2xl border border-slate-600/50"
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.99) 100%)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        minWidth: '600px',
        left: popoverPosition.left,
        top: popoverPosition.top
      }}
    >
      {partialPackageMessage && (
        <div className="px-4 py-3 border-b border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm">
          <p className="font-semibold mb-0.5">{lang === 'ar' ? 'تنبيه' : 'Partial package not allowed'}</p>
          <p className="text-amber-200/95">{partialPackageMessage}</p>
        </div>
      )}
      <BookingCalendar
        checkIn={checkIn}
        checkOut={checkOut}
        onChange={handleCalendarChange}
        onPartialPackageAttempt={() => setPartialPackageMessage(partialMsgText)}
        blockedDates={blockedDates}
        packages={packages}
        language={language}
        monthsAhead={monthsAhead}
        minNights={minNights}
        compact={false}
      />
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            setPartialPackageMessage('');
            setOpen((o) => !o);
          }
        }}
        disabled={disabled}
        className="w-full flex items-center gap-3 rounded-xl h-12 px-4 text-left bg-slate-700/60 border border-slate-600/80 hover:border-slate-500 focus:border-[#D5B577] focus:ring-1 focus:ring-[#D5B577]/30 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg
          className="w-5 h-5 flex-shrink-0 text-slate-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className={checkIn && checkOut ? 'text-white font-medium text-sm' : 'text-slate-400 text-sm'}>
          {label}
        </span>
        <svg
          className={`w-4 h-4 flex-shrink-0 text-slate-400 ml-auto transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {typeof document !== 'undefined' && createPortal(popoverContent, document.body)}
    </div>
  );
};

export default DateFieldWithCalendar;
